import { createFileRoute, Link } from '@tanstack/react-router';
import {
  Badge,
  Card,
  Group,
  Loader,
  SimpleGrid,
  Stack,
  Text,
  ThemeIcon,
  Title,
} from '@mantine/core';
import {
  IconAdjustments,
  IconBell,
  IconDeviceMobile,
  IconKey,
  IconLock,
  IconMoon,
  IconPlug,
  IconShieldCheck,
  IconSun,
  IconUser,
} from '@tabler/icons-react';
import { useQuery } from '@tanstack/react-query';
import { useMantineColorScheme } from '@mantine/core';
import * as devicesApi from '@/features/users/devices';
import { listApiKeys } from '@/features/api-keys/api';
import { useCurrentUserId } from '@/shared/hooks/useCurrentUserId';
import { useAuth } from '@/app/providers/AuthProvider';

export const Route = createFileRoute('/_app/settings/')({ component: SettingsIndex });

interface CardDef {
  to: string;
  label: string;
  desc: string;
  icon: typeof IconUser;
  color: string;
  /** badge live, computed via React in the cell */
  badgeKey: 'profile' | 'devices' | 'apiKeys' | 'theme' | 'notifications' | 'security' | 'privacy' | 'integrations' | 'preferences';
}

const CARDS: CardDef[] = [
  { to: '/settings/profile', label: 'Profilo', desc: 'Anagrafica e avatar', icon: IconUser, color: 'blue', badgeKey: 'profile' },
  { to: '/settings/preferences', label: 'Preferenze', desc: 'Lingua, tema, fuso orario', icon: IconAdjustments, color: 'teal', badgeKey: 'preferences' },
  { to: '/settings/notification-preferences', label: 'Notifiche', desc: 'Canali e categorie', icon: IconBell, color: 'cyan', badgeKey: 'notifications' },
  { to: '/settings/security', label: 'Sicurezza', desc: 'Password e accessi', icon: IconLock, color: 'orange', badgeKey: 'security' },
  { to: '/settings/devices', label: 'Dispositivi', desc: 'Sessioni e device attivi', icon: IconDeviceMobile, color: 'grape', badgeKey: 'devices' },
  { to: '/settings/api-keys', label: 'API Keys', desc: 'Token integrazione', icon: IconKey, color: 'yellow', badgeKey: 'apiKeys' },
  { to: '/settings/integrations', label: 'Integrazioni', desc: 'Webhook e servizi esterni', icon: IconPlug, color: 'indigo', badgeKey: 'integrations' },
  { to: '/settings/privacy', label: 'Privacy / GDPR', desc: 'Export dati e cancellazione', icon: IconShieldCheck, color: 'red', badgeKey: 'privacy' },
];

function SettingsIndex() {
  const userId = useCurrentUserId();
  const { user } = useAuth();
  const { colorScheme } = useMantineColorScheme();

  const devicesQ = useQuery({
    queryKey: ['my-devices', userId],
    queryFn: () => devicesApi.fetchMyDevices(userId),
    enabled: Boolean(userId),
    staleTime: 60_000,
  });

  const apiKeysQ = useQuery({
    queryKey: ['api-keys'],
    queryFn: listApiKeys,
    staleTime: 60_000,
  });

  const badgeFor = (key: CardDef['badgeKey']): React.ReactNode => {
    switch (key) {
      case 'profile':
        return user?.email ? (
          <Badge variant="light" size="xs" color="gray">{user.email}</Badge>
        ) : null;
      case 'devices': {
        if (devicesQ.isLoading) return <Loader size={12} />;
        const n = (devicesQ.data as unknown as { data?: unknown[] })?.data?.length
          ?? (Array.isArray(devicesQ.data) ? devicesQ.data.length : 0);
        return (
          <Badge variant="light" size="xs" color={n > 0 ? 'grape' : 'gray'}>
            {n} {n === 1 ? 'device' : 'device'}
          </Badge>
        );
      }
      case 'apiKeys': {
        if (apiKeysQ.isLoading) return <Loader size={12} />;
        const n = Array.isArray(apiKeysQ.data) ? apiKeysQ.data.length : 0;
        return (
          <Badge variant="light" size="xs" color={n > 0 ? 'yellow' : 'gray'}>
            {n} attive
          </Badge>
        );
      }
      case 'preferences':
        return (
          <Badge
            variant="light"
            size="xs"
            color="teal"
            leftSection={colorScheme === 'dark' ? <IconMoon size={10} /> : <IconSun size={10} />}
          >
            {colorScheme === 'dark' ? 'Dark' : colorScheme === 'light' ? 'Light' : 'Auto'}
          </Badge>
        );
      case 'notifications':
        return <Badge variant="light" size="xs" color="cyan">Configura</Badge>;
      case 'security':
        return <Badge variant="light" size="xs" color="orange">2FA off</Badge>;
      case 'privacy':
        return <Badge variant="light" size="xs" color="red">GDPR</Badge>;
      case 'integrations':
        return <Badge variant="light" size="xs" color="indigo">Webhook</Badge>;
      default:
        return null;
    }
  };

  return (
    <Stack>
      <div>
        <Title order={1}>Impostazioni</Title>
        <Text c="dimmed" size="sm">Profilo, sicurezza, integrazioni e privacy.</Text>
      </div>
      <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }}>
        {CARDS.map((c) => (
          <Card
            key={c.to}
            withBorder
            padding="lg"
            radius="md"
            component={Link}
            to={c.to}
            style={{ textDecoration: 'none' }}
          >
            <Group justify="space-between" mb="sm">
              <ThemeIcon size="xl" radius="md" variant="light" color={c.color}>
                <c.icon size={26} />
              </ThemeIcon>
              {badgeFor(c.badgeKey)}
            </Group>
            <Text fw={600} c="var(--mantine-color-text)">{c.label}</Text>
            <Text size="sm" c="dimmed" mt={2}>{c.desc}</Text>
          </Card>
        ))}
      </SimpleGrid>
    </Stack>
  );
}
